import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import { characterClassSkinApi as skinApi } from './characterClassSkin.api';
import { showToast } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const storeCharacterClassSkinApi = createAsyncThunk(
    'cardSaga/characterClassSkin/storeCharacterClassSkinApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { characterClassId, navigate, rawValues: values, resetForm, setResetCount } = params;
        try {
            dispatch(requestStarted());
            const formData = new FormData();
            formData.append('name', values.name);
            formData.append('rarity', values.rarity);
            formData.append('size', values.size);
            formData.append('image', values.image);

            const skin = await skinApi.store(characterClassId, formData);
            showToast('Submit Successfully');
            resetForm();
            setResetCount((prev) => ++prev);

            return skin;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue(); //`return` will dispatch `RejectedWithValueAction`. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateCharacterClassSkinApi = createAsyncThunk(
    'cardSaga/characterClassSkin/updateCharacterClassSkinApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { characterClassId, characterClassSkinId, navigate, values } = params;
        try {
            dispatch(requestStarted());
            const formData = new FormData();
            formData.append('name', values.name);
            formData.append('rarity', values.rarity);
            formData.append('size', values.size);

            if (values.image instanceof File) {
                formData.append('image', values.image);
            } else {
                formData.append('image', '');
            }

            const skin = await skinApi.update(characterClassId, characterClassSkinId, formData);
            showToast('Submit Successfully');

            return skin;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue(); //`return` will dispatch `RejectedWithValueAction`. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteCharacterClassSkinApi = createAsyncThunk(
    'cardSaga/characterClassSkin/deleteCharacterClassSkinApi',
    async (params, { dispatch }) => {
        const { characterClassId, characterClassSkinId, navigate } = params;

        try {
            await skinApi.delete(characterClassId, characterClassSkinId, navigate);

            return characterClassSkinId;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
        }
    },
);

export const characterClassSKinSlice = createSlice({
    name: 'cardSaga/characterClassSkin',
    initialState: {
        skins: [],
        rarities: [],
        sizes: [],
    },
    reducers: {
        setSkins(state, { payload }) {
            state.skins = payload;
        },
        setRarities(state, { payload }) {
            state.rarities = payload;
        },
        setSizes(state, { payload }) {
            state.sizes = payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(storeCharacterClassSkinApi.fulfilled, (state, { payload }) => {
                state.skins.unshift(payload);
            })
            .addCase(storeCharacterClassSkinApi.rejected, () => {});

        builder
            .addCase(updateCharacterClassSkinApi.fulfilled, (state, { payload }) => {
                if (state.skins.length === 0) return;

                state.skins.forEach((item, i, arr) => {
                    if (item.id === payload.id) {
                        arr[i] = payload;
                    }
                });
            })
            .addCase(updateCharacterClassSkinApi.rejected, () => {});

        builder
            .addCase(deleteCharacterClassSkinApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    skins: state.skins.filter(({ id }) => id !== payload),
                };
            })
            .addCase(deleteCharacterClassSkinApi.rejected, () => {});
    },
});

export const { setSkins, setRarities, setSizes } = characterClassSKinSlice.actions;
